import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

/* Normal CDF via approximation */
function erf(x) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * x);
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429;
  const y = 1 - (((((a5*t + a4)*t) + a3)*t + a2)*t + a1)*t * Math.exp(-x*x);
  return sign * y;
}
const normalCDF = z => 0.5 * (1 + erf(z / Math.sqrt(2)));
const normalPDF = z => Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);

/* Diagram: normal curve with shaded rejection region matching selected tail */
function PValueDiagram({ z, tail }) {
  const W = 420, H = 200, pad = 30;
  const xMin = -4, xMax = 4;
  const tx = x => pad + (x - xMin) / (xMax - xMin) * (W - 2 * pad);
  const pmax = normalPDF(0);
  const ty = y => H - pad - (y / pmax) * (H - 2 * pad - 12);

  const pts = [];
  for (let i = 0; i <= 200; i++) { const x = xMin + (i / 200) * (xMax - xMin); pts.push([tx(x), ty(normalPDF(x))]); }
  const curve = pts.map(p => p.join(',')).join(' ');

  const shade = (lo, hi, colour = '#ef4444') => {
    const seg = [];
    for (let i = 0; i <= 200; i++) {
      const x = xMin + (i / 200) * (xMax - xMin);
      if (x >= lo && x <= hi) seg.push([tx(x), ty(normalPDF(x))]);
    }
    if (seg.length < 2) return null;
    const d = `M${seg[0][0]},${H - pad} ` + seg.map(p => `L${p[0]},${p[1]}`).join(' ') + ` L${seg[seg.length - 1][0]},${H - pad} Z`;
    return <path d={d} fill={colour} opacity="0.55" />;
  };

  let regions = null;
  if (tail === 'left') regions = shade(xMin, Math.min(xMax, z));
  else if (tail === 'right') regions = shade(Math.max(xMin, z), xMax);
  else regions = <>{shade(xMin, -Math.abs(z))}{shade(Math.abs(z), xMax)}</>;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {/* x-axis */}
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#334155" strokeWidth="1.2" />
      {/* mean (0) marker */}
      <line x1={tx(0)} y1={pad} x2={tx(0)} y2={H - pad} stroke="#94a3b8" strokeDasharray="3 3" />
      {/* z marker(s) */}
      {tail === 'two' ? (
        <>
          <line x1={tx(-Math.abs(z))} y1={pad} x2={tx(-Math.abs(z))} y2={H - pad} stroke="#dc2626" strokeWidth="1.5" strokeDasharray="4 3" />
          <line x1={tx(Math.abs(z))} y1={pad} x2={tx(Math.abs(z))} y2={H - pad} stroke="#dc2626" strokeWidth="1.5" strokeDasharray="4 3" />
        </>
      ) : (
        <line x1={tx(z)} y1={pad} x2={tx(z)} y2={H - pad} stroke="#dc2626" strokeWidth="1.5" strokeDasharray="4 3" />
      )}
      {regions}
      <polyline points={curve} fill="none" stroke="#1e40af" strokeWidth="2" />
      {/* x-axis labels */}
      {[-3, -2, -1, 0, 1, 2, 3].map(v => (
        <g key={v}>
          <line x1={tx(v)} y1={H - pad} x2={tx(v)} y2={H - pad + 4} stroke="#334155" />
          <text x={tx(v)} y={H - pad + 15} fontSize="10" fill="#64748b" textAnchor="middle">{v}</text>
        </g>
      ))}
      {/* z labels */}
      {tail === 'two' ? (
        <>
          <text x={tx(-Math.abs(z)) + 3} y={pad + 10} fontSize="11" fill="#dc2626">-|z|={(-Math.abs(z)).toFixed(2)}</text>
          <text x={tx(Math.abs(z)) + 3} y={pad + 10} fontSize="11" fill="#dc2626">|z|={Math.abs(z).toFixed(2)}</text>
        </>
      ) : (
        <text x={tx(z) + 3} y={pad + 10} fontSize="11" fill="#dc2626">z = {z}</text>
      )}
      <text x={tx(-3)} y={pad + 25} fontSize="11" fill="#ef4444" opacity="0.7">Rejection region (red)</text>
    </svg>
  );
}

export default function PValueCalc() {
  const [z, setZ] = useState(1.96);
  const [tail, setTail] = useState('two');
  const [showWork, setShowWork] = useState(false);
  const c = useMemo(() => {
    const cdf = normalCDF(+z || 0);
    const one = 1 - cdf; // upper tail
    const lower = cdf;
    const two = 2 * Math.min(cdf, 1 - cdf);
    return { cdf, lower, one, two };
  }, [z]);
  const pValue = tail === 'left' ? c.lower : tail === 'right' ? c.one : c.two;
  return (
    <CalcShell look="cnet" title="P-value Calculator" subtitle="Compute a p-value from a z-score for one- or two-tailed tests.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Z-score</label><input type="number" step="0.01" value={z} onChange={e=>setZ(+e.target.value||0)} data-testid="pv-z"/></div>
          <div className="field"><label>Tail</label>
            <select value={tail} onChange={e=>setTail(e.target.value)} data-testid="pv-tail">
              <option value="two">Two-tailed (|Z|)</option>
              <option value="left">Left-tailed (Z ≤ z)</option>
              <option value="right">Right-tailed (Z ≥ z)</option>
            </select>
          </div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">P-value</div><div className="result-value" data-testid="pv-result">{pValue.toFixed(6)}</div></div>
          <table className="data-table">
            <tbody>
              <tr><td>Cumulative probability P(Z ≤ z) = Φ(z)</td><td className="text-right">{c.cdf.toFixed(6)}</td></tr>
              <tr><td>P(Z ≥ z) = 1 − Φ(z)</td><td className="text-right">{c.one.toFixed(6)}</td></tr>
              <tr><td>P(0 &lt; x &lt; Z) = Φ(|z|) − 0.5</td><td className="text-right">{(normalCDF(Math.abs(+z || 0)) - 0.5).toFixed(6)}</td></tr>
              <tr><td>P(−Z &lt; x &lt; Z) = 2·Φ(|z|) − 1</td><td className="text-right">{(2 * normalCDF(Math.abs(+z || 0)) - 1).toFixed(6)}</td></tr>
              <tr><td>P(|Z| ≥ |z|) — two-tailed</td><td className="text-right font-semibold">{c.two.toFixed(6)}</td></tr>
              <tr><td>Significance at α = 0.05?</td><td className="text-right">{pValue < 0.05 ? <span className="text-green-700 font-bold">Yes — reject H₀</span> : <span className="text-red-700 font-bold">No — fail to reject</span>}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Diagram */}
      <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid="pv-diagram">
        <h3 className="text-sm font-bold text-slate-900 mb-2">Standard Normal Distribution — {tail}-tailed rejection region</h3>
        <PValueDiagram z={+z || 0} tail={tail} />
      </div>

      <StepsPanel steps={[
        `Use the standard normal CDF Φ(z) = 0.5·[1 + erf(z/√2)]`,
        `Φ(${z}) ≈ ${c.cdf.toFixed(6)}`,
        `${tail === 'two' ? 'Two-tailed p = 2·min(Φ, 1−Φ)' : tail === 'left' ? 'Left tail p = Φ' : 'Right tail p = 1−Φ'} = <b>${pValue.toFixed(6)}</b>`,
      ]}/>

      <div className="mt-4">
        <button onClick={() => setShowWork(v => !v)} data-testid="pv-show-work-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showWork ? '− Hide working' : '+ Show working'}
        </button>
        {showWork && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="pv-work-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
            <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
              <li><b>Compute the cumulative probability</b> Φ(z) using the standard normal CDF: <span className="font-mono">Φ({z}) = ½ · [1 + erf({z}/√2)] = {c.cdf.toFixed(6)}</span></li>
              <li><b>Left tail (Z ≤ z)</b>: p = Φ(z) = {c.lower.toFixed(6)}</li>
              <li><b>Right tail (Z ≥ z)</b>: p = 1 − Φ(z) = {c.one.toFixed(6)}</li>
              <li><b>Two-tailed (|Z| ≥ |z|)</b>: p = 2·min(Φ, 1−Φ) = {c.two.toFixed(6)}</li>
              <li><b>Compare to α = 0.05</b>: {pValue < 0.05 ? 'p < α → reject the null hypothesis.' : 'p ≥ α → fail to reject the null.'}</li>
              <li><b>Final</b>: <b className="text-[#4c7b25]">p = {pValue.toFixed(6)}</b></li>
            </ol>
          </div>
        )}
      </div>
    </CalcShell>
  );
}
